import { useEffect, useMemo, useState } from 'react';

// Components
import { ConfigContext, SizeProvider, Standings } from '@velinsi/smp-widgets-ui';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';

// Hooks
import useTennisCompetition from '../../hooks/useTennisCompetition';
import usePlayoff from '../../hooks/useTennisPlayoff';

// Types
import {
  BaseWidgetProps,
  INormalizedPlayoffData,
  INormalizedTennisCompetitionDetails,
  ISeason,
  IStage,
  ITennisPlayoffConfig,
  ITournament,
} from '../../../../../packages/types';

// Misc
import { normalizePlayoffData } from '@sportal365/smp-widget-normalizers';
import { defaultHandleEventClick, defaultHandleParticipantClick, defaultHandleTournamentClick } from '@sportal365/smp-widget-utils';
import { PLAYER_ENTITY } from '../../../../../packages/constants/entityTypes';
import { Sports } from '../../../../../packages/constants/sportType';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import { normalizeCompetitionDetailsData } from '../../normalizers/normalizeTennisCompetitionData';

export type PlayoffWidgetProps = BaseWidgetProps<ITennisPlayoffConfig>;

export const TennisPlayoffWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: PlayoffWidgetProps) => {
  const [seasonId, setSeasonId] = useState<string | undefined>(config?.dataSeason);
  const [stageId, setStageId] = useState<string | undefined>(config?.dataStage);
  const { ref } = useRootNodeRef();

  const { competition, tournaments, seasonYears } = useTennisCompetition({
    dataCompetition: config?.dataCompetition,
    season: seasonId,
  });

  useEffect(() => {
    const foundStage = tournaments?.find(({ id }) => id === stageId);

    if (foundStage) {
      return;
    }

    setStageId(tournaments?.[0]?.id);
  }, [stageId, tournaments]);

  const normalizedCompetition = useMemo(
    () =>
      normalizeCompetitionDetailsData({
        competition,
        tournaments,
        seasonYears,
        dataEntityLinks: config?.dataEntityLinks,
      }),
    [competition, config, tournaments, seasonYears],
  ) as INormalizedTennisCompetitionDetails;

  const handlePlayerClick = (id: string | number, name: string) =>
    defaultHandleParticipantClick(handleClick, { id, name, entityType: PLAYER_ENTITY });

  const handleTournamentClick = () =>
    defaultHandleTournamentClick(handleClick, normalizedCompetition?.competition.id, normalizedCompetition?.competition.name);

  const handleEventClick = (event: string | number) => defaultHandleEventClick(handleClick, event);

  const handleSeasonChange = (value?: string) => {
    setSeasonId(value);
    setStageId(undefined);
  };

  const handleActiveStageChange = (stageId: string) => setStageId(stageId);

  // Playoff/knockout logic
  const { playoffStage, handleClick, showWidget, isLoading } = usePlayoff({
    tournamentId: stageId,
    subscribers,
    dataRefreshTime: config?.dataRefreshTime,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataWidgetCuid: cuid,
  });
  const normalizedPlayoffData: INormalizedPlayoffData | null = normalizePlayoffData({
    stage: playoffStage,
    dataEntityLinks: config?.dataEntityLinks,
    dataLabels: config?.dataLabels,
    sport: Sports.TENNIS,
  });

  if (!showWidget) return null;

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider
        isLoading={isLoading}
        slickStyles={true}
        shadow={shadow}
        themes={themes}
        dataTheme={config?.dataTheme}
        css={{ backgroundColor: '$primaryBackgroundColor' }}
        refForShadow={ref}
      >
        <SizeProvider>
          {normalizedCompetition?.tournaments && normalizedCompetition?.tournaments?.length > 0 && (
            <Standings
              stageId={stageId}
              seasonId={seasonId}
              playoffStage={normalizedPlayoffData}
              stageList={normalizedCompetition?.tournaments as IStage[]}
              competition={{ ...(normalizedCompetition?.competition as ITournament), seasons: normalizedCompetition?.seasons as ISeason[] }}
              standings={[]}
              dataLabels={config?.dataLabels}
              dataHeaderDisplay={config?.dataHeaderDisplay}
              dataOptionsDisplay={config?.dataOptionsDisplay}
              handleTeamClick={handlePlayerClick}
              handleSeasonChange={handleSeasonChange}
              handleTournamentClick={handleTournamentClick}
              handleActiveStageChange={handleActiveStageChange}
              handleEventClick={handleEventClick}
              sport={Sports.TENNIS}
              handleSubscriptionClick={handleClick}
              isNational={true}
            />
          )}
        </SizeProvider>
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
